import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  Loader2,
  Lock,
  LockOpen,
  MapPin,
  Sparkles,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { adminFetch } from '@/hooks/admin/useAdminApi';
import {
  useRefreshCustomerCollection,
  useSetCreditHold,
  useSetCustomerActive,
} from '@/hooks/admin/useAdminCustomers';
import type { AdminCustomerLocation } from '@/hooks/admin/useAdminCustomerDetail';
import { useCustomerDetailContext } from '@/pages/admin/AdminCustomerDetail';
import { PaymentProfileCard } from './PaymentDocsPanels';
import { ChainGroupCard } from './ChainGroupCard';
import { GroupInheritanceBanner } from './GroupInheritanceBanner';
import FoodservicePortalCard from './FoodservicePortalCard';
import TankRentalCard from './TankRentalCard';
import StoreCodeCard from './StoreCodeCard';
import StoreContactsCard from './StoreContactsCard';

export default function OverviewTab() {
  const { customer, refetch } = useCustomerDetailContext();
  const setActive = useSetCustomerActive();
  const creditHold = useSetCreditHold();
  const refresh = useRefreshCustomerCollection();

  const onHold = customer.on_credit_hold === true;
  const afterMutate = () => refetch();

  return (
    <div className="space-y-5">
      {/* Member store of a Master-billing group → settings inherit from the
          master; edits here become store overrides. */}
      <GroupInheritanceBanner customerId={customer.id} />

      {/* Account & payments — ONE merged section: the old facts box (QBO ID /
          Account # / Terms / Credit limit) folded into the payment profile
          (terms + per-category methods + Stripe status + request email). */}
      <PaymentProfileCard
        customerId={customer.id}
        facts={{
          qboId: customer.qbo_customer_id,
          accountNumber: customer.account_number,
          creditLimit: customer.credit_limit,
        }}
      />

      {/* Chain member stores only — the number an emailed PO routes on. */}
      <StoreCodeCard
        customerId={customer.id}
        storeCode={customer.store_code}
        onSaved={afterMutate}
      />

      {/* Status + lifecycle actions */}
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 space-y-3">
        <h2 className="font-display text-sm font-semibold text-brix-ink">Status</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {onHold ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={creditHold.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Unlock ordering for ${customer.name}? The customer will be able to place orders again.`,
                  )
                ) {
                  creditHold.mutate(
                    { customer_id: customer.id, on_credit_hold: false },
                    { onSuccess: afterMutate },
                  );
                }
              }}
            >
              {creditHold.isPending ? <Loader2 size={12} className="animate-spin" /> : <LockOpen size={12} />}
              Unlock ordering
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-700 hover:bg-red-50"
              disabled={creditHold.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Lock ordering for ${customer.name}? They will be blocked from placing any new orders until you unlock them.`,
                  )
                ) {
                  creditHold.mutate(
                    { customer_id: customer.id, on_credit_hold: true },
                    { onSuccess: afterMutate },
                  );
                }
              }}
            >
              {creditHold.isPending ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
              Lock ordering
            </Button>
          )}

          {customer.active ? (
            // Deactivation goes through the guided closure checklist —
            // equipment / tanks / balances / refunds must clear first.
            <Button variant="secondary" size="sm" asChild>
              <Link to="../closure">
                <Archive size={12} />
                Deactivate / close account…
              </Link>
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              disabled={setActive.isPending}
              onClick={() =>
                setActive.mutate(
                  { customer_id: customer.id, active: true },
                  { onSuccess: afterMutate },
                )
              }
            >
              {setActive.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
              Reactivate
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate(customer.id, { onSuccess: afterMutate })}
            title="Re-import pricing + My Collection from QBO invoice history"
          >
            {refresh.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {refresh.isPending ? 'Importing…' : 'Refresh from invoices'}
          </Button>
        </div>
      </div>

      {/* "Switched over to the new system" tracker — manual checkbox only
          (the Bill & Pay integration is gone; this tracks which customers
          have been fully switched over / handled in the B&P console). */}
      <NewSystemCard />

      {/* Chain / franchise group — renders only when this QBO customer has
          sub-customers (or is already marked as a group master). */}
      <ChainGroupCard customerId={customer.id} customerName={customer.name} />

      {/* Which lane this account's EQUIPMENT runs in. Chain accounts
          requisition + fulfil in the foodservice portal (ERLS handles only
          the lease); independents buy through ERLS and never see it. */}
      <FoodservicePortalCard customerId={customer.id} />

      {/* Monthly CO₂ tank rental — the switch that puts this account on the
          BTRF billing run. The same row the audit portal and Refractor flip. */}
      <TankRentalCard qboCustomerId={customer.qbo_customer_id} />

      {/* Delivery days + order reminders live on their own Locations & deliveries tab. */}

      {/* Who to call about this store. Contacts only — no portal access. */}
      <StoreContactsCard customerId={customer.id} />

      {/* Locations / address */}
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-sm font-semibold text-brix-ink">
            {customer.location_mode === 'master' ? 'Locations' : 'Delivery address'}
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-brix-muted">
              {customer.location_mode === 'master'
                ? `${customer.locations.length} location${customer.locations.length === 1 ? '' : 's'}`
                : '1:1 account'}
            </span>
            <Link
              to="../locations"
              relative="path"
              className="text-[11px] font-medium text-brix-primary hover:underline"
            >
              Manage →
            </Link>
          </div>
        </div>

        {customer.location_mode === 'master' ? (
          customer.locations.length === 0 ? (
            <EmptyLocations />
          ) : (
            <div className="space-y-2">
              {customer.locations.map((loc) => (
                <LocationCard key={loc.id} loc={loc} />
              ))}
            </div>
          )
        ) : customer.primary_location ? (
          <LocationCard loc={customer.primary_location} />
        ) : (
          <EmptyLocations />
        )}
      </div>
    </div>
  );
}

/**
 * "Switched over to the new system" — the manual cutover tracker. Checking
 * the box records that this customer has been fully moved off Bill & Pay
 * (handled by hand in the B&P console) and onto Stripe. No B&P API behind
 * it — the integration is gone; this is bookkeeping. Audit-logged.
 */
function NewSystemCard() {
  const { customer, refetch } = useCustomerDetailContext();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const done = customer.on_new_system;

  async function setFlag(value: boolean) {
    setBusy(true);
    setErr(null);
    try {
      await adminFetch('admin-set-new-system', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customer.id, value }),
      });
      refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`rounded-xl border px-5 py-4 space-y-2 ${
        done ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'
      }`}
    >
      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={done}
          disabled={busy}
          onChange={(e) => {
            const v = e.target.checked;
            if (
              window.confirm(
                v
                  ? `Mark ${customer.name} as switched over to the new system?`
                  : `Un-mark ${customer.name}? The account will show as still in transition.`,
              )
            ) {
              void setFlag(v);
            }
          }}
          className="mt-0.5 h-4 w-4 accent-emerald-600"
        />
        <span>
          <span className="text-sm font-semibold text-brix-ink">
            Switched over to the new system
          </span>
          <span className="block text-[11px] text-brix-muted mt-0.5">
            {done
              ? 'Fully on Stripe — nothing legacy keeps charging or emailing them.'
              : 'Still in transition — check this once their Bill & Pay account has been turned off in the B&P console.'}
          </span>
        </span>
      </label>

      {customer.bp_unenroll_note && (
        <p className="text-[11px] text-brix-muted">
          {customer.bp_unenroll_note}
          {customer.bp_unenrolled_at &&
            ` · ${new Date(customer.bp_unenrolled_at).toLocaleString()}`}
        </p>
      )}

      {err && <p className="text-xs text-brix-warning">{err}</p>}
    </div>
  );
}

function LocationCard({ loc }: { loc: AdminCustomerLocation }) {
  const addr = [loc.address_line1, loc.address_line2].filter(Boolean).join(', ');
  const cityLine = [loc.city, loc.state].filter(Boolean).join(', ');
  const full = [addr, cityLine, loc.zip].filter(Boolean).join(' · ');
  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2.5">
      <div className="flex items-start gap-2">
        <MapPin size={14} className="text-brix-primary mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-brix-ink truncate">
            {loc.display_name}
            {loc.location_code && (
              <span className="ml-1.5 text-[11px] text-brix-muted">{loc.location_code}</span>
            )}
            {!loc.active && <span className="ml-1.5 text-[11px] text-brix-muted">(inactive)</span>}
          </div>
          <div className="text-xs text-brix-muted">
            {full || <span className="italic">no address on file</span>}
          </div>
          {loc.delivery_notes && (
            <div className="text-[11px] text-brix-muted mt-0.5">Notes: {loc.delivery_notes}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyLocations() {
  return (
    <div className="text-sm text-brix-muted italic">
      No locations on file. Re-running Enable from the customers list, or “Refresh from invoices”,
      will repair the primary location.
    </div>
  );
}
